import { authInfo } from "@/core/auth";
import { getConfiguration } from "@/configuration/services/configuration.service";
import {
  getExecutionParallelConcurrency,
  sumParallelLoopThreadCount,
  toThreadBudget,
} from "@/flow/utils/loopUtils";

export const PARALLEL_PROCESSING_ENABLED_KEY = "processor.parallel.enabled";
export const PARALLEL_PROCESSING_CONCURRENCY_KEY =
  "processor.parallel.concurrency";

const TENANT_CONFIG_LEVEL = "TENANT";

export type ParallelThreadBudgetResult = {
  parallelProcessingEnabled: boolean;
  totalThreadCount: number;
  threadBudget?: number;
  exceeded: boolean;
};

async function getTenantConfigValue(key: string): Promise<unknown> {
  const tenantId = authInfo.value?.tenantId;
  if (!tenantId) return undefined;

  try {
    const config = await getConfiguration(key, TENANT_CONFIG_LEVEL, tenantId);
    return config?.value ?? config?.defaultValue;
  } catch (error) {
    console.warn(`Could not load parallel processing config '${key}'.`, error);
    return undefined;
  }
}

export async function isParallelProcessingEnabled(): Promise<boolean> {
  const value = await getTenantConfigValue(PARALLEL_PROCESSING_ENABLED_KEY);
  return String(value).trim().toLowerCase() === "true";
}

async function getDefaultParallelConcurrency(): Promise<number | undefined> {
  const value = await getTenantConfigValue(PARALLEL_PROCESSING_CONCURRENCY_KEY);
  return toThreadBudget(value);
}

export async function evaluateParallelThreadBudget(
  flow: any
): Promise<ParallelThreadBudgetResult> {
  const totalThreadCount = sumParallelLoopThreadCount(flow?.steps);
  const parallelProcessingEnabled = await isParallelProcessingEnabled();

  if (!parallelProcessingEnabled) {
    return {
      parallelProcessingEnabled,
      totalThreadCount,
      exceeded: false,
    };
  }

  const executionBudget = getExecutionParallelConcurrency(flow?.execution);
  const threadBudget =
    executionBudget !== undefined
      ? executionBudget
      : await getDefaultParallelConcurrency();

  return {
    parallelProcessingEnabled,
    totalThreadCount,
    threadBudget,
    exceeded: threadBudget !== undefined && totalThreadCount > threadBudget,
  };
}
